// Developed by Softvative.com
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useIncidents } from '../../context/IncidentContext';
import toast from 'react-hot-toast';
import api from '../../services/api';

const {
    FiArrowLeft, FiSave, FiX, FiEdit2, FiMessageSquare, FiPaperclip, FiClock,
    FiUser, FiCalendar, FiTag, FiAlertTriangle, FiCheckCircle, FiPlayCircle,
    FiSend, FiTrash2, FiUpload, FiImage
} = FiIcons;

const IncidentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { incidents, updateIncidentStatus, updateIncident, addComment, getCustomFieldValue, customFields, fetchIncidents } = useIncidents();

    const [incident, setIncident] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [attachedImages, setAttachedImages] = useState([]);

    useEffect(() => {
        const foundIncident = incidents.find(inc => inc.id === Number(id));
        if (foundIncident) {
            setIncident(foundIncident);
            setEditForm({
                title: foundIncident.title,
                description: foundIncident.description,
                assignedTo: foundIncident.assignedTo,
                assignee: foundIncident.assignee,
                priority: foundIncident.priority,
                customFieldValues: foundIncident.customFieldValues || {}
            });
        } else {
            navigate('/incidents');
        }
    }, [id, incidents, navigate]);

    const statusOptions = [
        { value: 'New', label: 'New', icon: FiAlertTriangle, color: 'blue' },
        { value: 'In Progress', label: 'In Progress', icon: FiPlayCircle, color: 'yellow' },
        { value: 'Resolved', label: 'Resolved', icon: FiCheckCircle, color: 'green' },
        { value: 'Closed', label: 'Closed', icon: FiX, color: 'gray' }
    ];

    const priorityColors = {
        'Critical': 'red',
        'High': 'orange',
        'Medium': 'yellow',
        'Low': 'green'
    };

    const handleStatusChange = (newStatus) => {
        updateIncidentStatus(incident.id, newStatus);
        setShowStatusDropdown(false);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        const payload = {
            ...incident,
            ...editForm,
            updatedAt: new Date()
        };

        console.log("update incident payload: ", payload);

        try {
            // Step 1: Update incident
            await updateIncident(payload.id, payload);

            // Step 2: Save custom field values (same logic as create) 
            if (customFields.length > 0) {
                const customFieldPayload = {
                    entityType: 'Incident',
                    entityId: payload.id,
                    fields: Object.entries(payload.customFieldValues)
                        .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
                        .map(([key, value]) => {
                            const field = customFields.find(f => f.fieldKey === key);
                            return {
                                key,
                                custom_field_id: field ? field.id : null,
                                value: typeof value === 'boolean' ? value.toString() : value
                            };
                        })
                        .filter(f => f.custom_field_id !== null)
                };

                if (customFieldPayload.fields.length > 0) {
                    try {
                        await api.post('/custom-fields/values', customFieldPayload);
                        console.log('✅ Custom fields updated successfully');
                    } catch (error) {
                        console.error('❌ Error updating custom fields:', error);
                        toast.error('Incident updated but custom fields failed to save');
                    }
                } else {
                    console.log('ℹ️ No custom field values to update');
                }
            }

            toast.success('Incident updated successfully');
            setIsEditing(false);

            // Optional: refresh data
            fetchIncidents();

        } catch (error) {
            console.error('❌ Error updating incident:', error);
            toast.error(error.message || 'Failed to update incident');
        }
    };

    const handleCancelEdit = () => {
        setEditForm({
            title: incident.title,
            description: incident.description,
            assignedTo: incident.assignedTo,
            assignee: incident.assignee,
            priority: incident.priority,
            customFieldValues: incident.customFieldValues || {}
        });
        setIsEditing(false);
    };

    const handleCommentSubmit = (e) => {
        e.preventDefault();
        if (newComment.trim()) {
            const comment = addComment(incident.id, newComment);
            setIncident(prev => ({
                ...prev,
                comments: [...(prev.comments || []), comment]
            }));
            setNewComment('');
        }
    };

    const handleEditInputChange = (field, value) => {
        if (field.startsWith('custom_')) {
            const fieldId = field.replace('custom_', '');
            setEditForm(prev => ({
                ...prev,
                customFieldValues: {
                    ...prev.customFieldValues,
                    [fieldId]: value
                }
            }));
        } else {
            setEditForm(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            url: URL.createObjectURL(file),
            file: file
        }));

        setAttachedImages(prev => [...prev, ...newImages]);
    };

    const removeImage = (imageId) => {
        setAttachedImages(prev => {
            const image = prev.find(img => img.id === imageId);
            if (image && image.url.startsWith('blob:')) {
                URL.revokeObjectURL(image.url);
            }
            return prev.filter(img => img.id !== imageId);
        });
    };

    const renderCustomField = (field, value, isEditable = false) => {
        if (isEditable) {
            switch (field.type) {
                case 'text':
                case 'number':
                case 'email':
                case 'url':
                case 'date':
                    return (
                        <input
                            type={field.type}
                            value={editForm.customFieldValues[field.id] || ''}
                            onChange={(e) => handleEditInputChange(`custom_${field.id}`, e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                        />
                    );

                case 'multiline':
                    return (
                        <textarea
                            rows={3}
                            value={editForm.customFieldValues[field.id] || ''}
                            onChange={(e) => handleEditInputChange(`custom_${field.id}`, e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                        />
                    );

                case 'select':
                    return (
                        <select
                            value={editForm.customFieldValues[field.id] || ''}
                            onChange={(e) => handleEditInputChange(`custom_${field.id}`, e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                        >
                            <option value="">Select an option</option>
                            {field.options.map((option, index) => (
                                <option key={index} value={option}>{option}</option>
                            ))}
                        </select>
                    );

                case 'radiobutton':
                    return (
                        <div className="space-y-2">
                            {field.options.map((option, index) => (
                                <label key={index} className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name={`edit_field_${field.id}`}
                                        value={option}
                                        checked={editForm.customFieldValues[field.id] === option}
                                        onChange={(e) => handleEditInputChange(`custom_${field.id}`, e.target.value)}
                                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{option}</span>
                                </label>
                            ))}
                        </div>
                    );

                case 'boolean':
                    return (
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={editForm.customFieldValues[field.id] || false}
                                onChange={(e) => handleEditInputChange(`custom_${field.id}`, e.target.checked)}
                                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{field.name}</span>
                        </label>
                    );

                default:
                    return null;
            }
        } else {
            switch (field.type) {
                case 'checkbox':
                    return value ? 'Yes' : 'No';
                default:
                    return value || '-';
            }
        }
    };

    if (!incident) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading incident...</p>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 h-full overflow-y-auto"
        >
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/incidents')}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{incident.id}</h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Created {formatDistanceToNow(new Date(incident.createdAt), { addSuffix: true })}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    {/* Status Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            <SafeIcon
                                icon={statusOptions.find(s => s.value === incident.status)?.icon || FiAlertTriangle}
                                className={`w-4 h-4 text-${statusOptions.find(s => s.value === incident.status)?.color || 'blue'}-600`}
                            />
                            <span className="font-medium text-gray-900 dark:text-white">{incident.status}</span>
                            <SafeIcon icon={FiX} className="w-4 h-4 text-gray-400 rotate-180" />
                        </button>

                        <AnimatePresence>
                            {showStatusDropdown && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                                >
                                    {statusOptions.map(status => (
                                        <button
                                            key={status.value}
                                            onClick={() => handleStatusChange(status.value)}
                                            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                            disabled={status.value === incident.status}
                                        >
                                            <SafeIcon icon={status.icon} className={`w-4 h-4 text-${status.color}-600`} />
                                            <span className="text-gray-900 dark:text-white">{status.label}</span>
                                            {status.value === incident.status && (
                                                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-600 ml-auto" />
                                            )}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Edit/Save/Cancel Buttons */}
                    {isEditing ? (
                        <div className="flex space-x-2">
                            <button
                                onClick={handleSaveEdit}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                <SafeIcon icon={FiSave} className="w-4 h-4" />
                                <span>Save</span>
                            </button>
                            <button
                                onClick={handleCancelEdit}
                                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                            >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                                <span>Cancel</span>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleEdit}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            <span>Edit</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Details Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incident Details</h2>

                        <div className="space-y-4">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Title
                                </label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editForm.title}
                                        onChange={(e) => handleEditInputChange('title', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                    />
                                ) : (
                                    <p className="text-gray-900 dark:text-white">{incident.title}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Description
                                </label>
                                {isEditing ? (
                                    <textarea
                                        rows={4}
                                        value={editForm.description}
                                        onChange={(e) => handleEditInputChange('description', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                    />
                                ) : (
                                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{incident.description}</p>
                                )}
                            </div>

                            {/* Custom Fields */}
                            {customFields.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {customFields.map(field => (
                                            <div key={field.id}>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    {field.name} {field.required && <span className="text-red-500">*</span>}
                                                </label>
                                                {renderCustomField(
                                                    field,
                                                    getCustomFieldValue(incident, field.id),
                                                    isEditing
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Attachments */}
                            {incident.attachments && incident.attachments.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Attachments</h3>
                                    <div className="space-y-2">
                                        {incident.attachments.map((attachment, index) => (
                                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center space-x-3">
                                                    <SafeIcon icon={FiPaperclip} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{attachment.name}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {(attachment.size / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* New Image Attachments */}
                            {isEditing && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Add Attachments
                                    </label>
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                            id="detail-image-upload"
                                        />
                                        <label
                                            htmlFor="detail-image-upload"
                                            className="cursor-pointer inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                        >
                                            <SafeIcon icon={FiUpload} className="w-4 h-4" />
                                            <span>Choose Images</span>
                                        </label>
                                    </div>

                                    {attachedImages.length > 0 && (
                                        <div className="mt-4 space-y-2">
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">New Attachments:</p>
                                            {attachedImages.map(image => (
                                                <div key={image.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <div className="flex items-center space-x-3">
                                                        <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{image.name}</p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                {(image.size / 1024).toFixed(1)} KB
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImage(image.id)}
                                                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Comments Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Comments</h2>

                        {/* Add Comment Form */}
                        <form onSubmit={handleCommentSubmit} className="mb-6">
                            <div className="flex space-x-3">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                                >
                                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                                    <span>Send</span>
                                </button>
                            </div>
                        </form>

                        {/* Comments List */}
                        <div className="space-y-4">
                            {incident.comments && incident.comments.length > 0 ? (
                                incident.comments.map(comment => (
                                    <div key={comment.id} className="flex space-x-3">
                                        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <span className="font-medium text-gray-900 dark:text-white">{comment.author}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 dark:text-gray-300 mt-1">{comment.text}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No comments yet</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Information Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Information</h3>

                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Priority</p>
                                {isEditing ? (
                                    <select
                                        value={editForm.priority}
                                        onChange={(e) => handleEditInputChange('priority', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                    >
                                        <option value="Critical">Critical</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </select>
                                ) : (
                                    <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full bg-${priorityColors[incident.priority]}-100 text-${priorityColors[incident.priority]}-800 dark:bg-${priorityColors[incident.priority]}-900 dark:text-${priorityColors[incident.priority]}-200`}>
                                        {incident.priority}
                                    </span>
                                )}
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Category</p>
                                <p className="font-medium text-gray-900 dark:text-white">{incident.category}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Service</p>
                                <p className="font-medium text-gray-900 dark:text-white">{incident.service}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Assignment</p>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <select
                                            value={editForm.assignedTo}
                                            onChange={(e) => handleEditInputChange('assignedTo', e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                        >
                                            <option value="">Select group</option>
                                            <option value="Network Team">Network Team</option>
                                            <option value="Application Team">Application Team</option>
                                            <option value="Database Team">Database Team</option>
                                            <option value="Security Team">Security Team</option>
                                            <option value="Hardware Team">Hardware Team</option>
                                        </select>
                                        <input
                                            type="text"
                                            value={editForm.assignee}
                                            onChange={(e) => handleEditInputChange('assignee', e.target.value)}
                                            placeholder="Assignee (optional)"
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">{incident.assignedTo || 'Unassigned'}</p>
                                        {incident.assignee && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{incident.assignee}</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Created by</p>
                                <p className="font-medium text-gray-900 dark:text-white">{incident.createdBy}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Created at</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {format(new Date(incident.createdAt), 'MMM dd, yyyy HH:mm')}
                                </p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Updated at</p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {format(new Date(incident.updatedAt), 'MMM dd, yyyy HH:mm')}
                                </p>
                            </div>

                            {incident.resolvedAt && (
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Resolved at</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {format(new Date(incident.resolvedAt), 'MMM dd, yyyy HH:mm')}
                                    </p>
                                </div>
                            )}

                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Tags</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {incident.tags.map(tag => (
                                        <span key={tag} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default IncidentDetail;